<template>
  <div>
    <!-- SECTION HEADLINE -->
    <div id="sectionTitle">
      <b-container>
        <b-row>
          <b-col></b-col>
          <b-col sm="12" md="10">
            <div class="sectionHeadline">
              <h1>Software Projects</h1>
              <p>
                Projects that helped me grow, learn, and get better at what I do
              </p>
            </div>
          </b-col>
          <b-col></b-col>
        </b-row>
      </b-container>
    </div>
    <!-- SOFTWARE PROJECTS -->
    <content-section theme="dark">
      <b-row>
        <b-col class="text-center">
          <h1>Highlighted Projects</h1>
          <p>
            Welcome to a showcase of some of my best software projects to date
          </p>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 1 -->
          <info-card id="ymc-project" theme="dark">
            <h2 slot="title">My Personal Website</h2>
            <p slot="tagline">
              Very first effort to put my mark on the world wide web |
              <strong>https://yosephmartin.com</strong>
            </p>
            <b-carousel slot="custom-carousel" controls indicators>
              <b-carousel-slide
                v-for="(item, i) in ymcCarousel"
                :key="i"
                :img-src="item.src"
              >
              </b-carousel-slide>
            </b-carousel>
            <div slot="description">
              <p>
                What started back in 2018 as a simple way to document my
                development journey has grown into something much bigger. Over
                the years, my personal website has gone through several major
                updates, evolving alongside me.
              </p>
              <p>
                Every time I hit a milestone—big or small—this site is the first
                to know. What began as a space to showcase achievements has
                slowly become something more personal, a reflection of who I am
                and what I care about.
              </p>
              <p>
                Feel free to look around—this is my little corner of the
                internet. Hope you enjoy your stay!
              </p>
              <p class="highlighted">
                Utilized: HTML, CSS, JavaScript, Vue.js, BootstrapVue, Hostinger
              </p>
            </div>
            <b-button slot="button" class="button-dark big-button" disabled
              ><strong>Look who’s here—awesome!</strong>
            </b-button>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 2 -->
          <info-card id="stufftodo-project" theme="dark">
            <h2 slot="title">Stuff To-Do</h2>
            <p slot="tagline">
              Every one of us has a lot of #StuffToDo |
              <strong>https://mystufftodo.com</strong>
            </p>
            <b-img
              center
              slot="custom-image"
              :src="stuffToDoImage"
              fluid
              alt="Stuff To-Do"
            ></b-img>
            <div slot="description">
              <p>
                Stuff To-Do is a cross-platform to-do list app built for
                collaboration, available on both web and Android. It features
                real-time data sync across platforms, so your tasks stay
                up-to-date no matter where you use it.
              </p>
              <p>
                You can easily manage personal tasks or work with others on
                shared lists. The personal list lets you create and organize
                your own to-dos just the way you like them.
              </p>
              <p>
                But the real magic is in the collaborative list—it’s the heart
                of Stuff To-Do. Designed with teamwork in mind, it includes
                collaborator role management to make working together smoother
                and more organized.
              </p>
              <p>
                With Stuff To-Do, we’re aiming to change the way you see to-do
                lists. It’s more than just checking boxes—it’s a smarter, more
                connected way to get things done.
              </p>
              <p class="highlighted">
                Utilized: HTML, CSS, JavaScript, Vue.js, BootstrapVue, Android,
                Java, XML, cPanel, Firebase Firestore
              </p>
            </div>
            <b-button
              slot="button"
              class="button-dark big-button"
              @click="openLink('https://mystufftodo.com')"
              >Give it a try: <strong>mystufftodo.com</strong>
            </b-button>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 3 -->
          <info-card theme="dark">
            <h2 slot="title">Galaxy Shooter</h2>
            <p slot="tagline">
              A futuristic sci-fi galactic shooter against the unknown enemy in
              the deep space
            </p>
            <b-carousel slot="custom-carousel" controls indicators>
              <b-carousel-slide
                v-for="(item, i) in galaxyShooterCarousel"
                :key="i"
                :img-src="item.src"
              >
              </b-carousel-slide>
            </b-carousel>
            <div slot="description">
              <p>
                Galaxy Shooter is a fast-paced 2D arcade game set in outer
                space, where players engage in thrilling spaceship battles. The
                game was developed as part of the Unity Authorized Course for
                game developer training.
              </p>
              <p class="highlighted">Utilized: C#, Unity</p>
              <p>How to play:</p>
              <ul>
                <li>Press Spacebar to start the game</li>
                <li>Use W, A, S, D keys to move your spaceship</li>
                <li>Click the left mouse button to shoot</li>
                <li>Collect power-ups and see how long you can survive!</li>
              </ul>
            </div>
            <b-button
              slot="button"
              class="button-dark big-button"
              @click="
                openLink(
                  'https://yosephmartin.com/demo/space-shooter/index.html'
                )
              "
              >Play the game!
            </b-button>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 4 -->
          <info-card id="prolog-project" theme="dark">
            <h2 slot="title">ProLog</h2>
            <p slot="tagline">
              A biometric authentication app designed to give you peace of mind
              when it comes to data security
            </p>
            <b-img
              center
              slot="custom-image"
              :src="prologImage"
              fluid
              alt="ProLog"
            ></b-img>
            <div slot="description">
              <p>
                ProLog is an Android app that adds an extra layer of security
                through two-factor authentication, combining your chosen method
                (Password, PIN, or Pattern) with biometric verification.
              </p>
              <p>
                After passing the first step, you’ll verify your identity using
                one of the following biometrics:
              </p>
              <ul>
                <li>Fingerprint</li>
                <li>Voice*</li>
                <li>Face recognition**</li>
              </ul>
              <p>
                If both checks are successful, you’ll gain access to your secure
                data
              </p>
              <p>
                * Voice authentication compares a customizable passphrase—not
                the unique characteristics of your voice
              </p>
              <p>** Face recognition will be available in a future update</p>
              <p class="highlighted">Utilized: Android, Java, XML</p>
            </div>
            <b-button
              slot="button"
              class="button-dark big-button"
              @click="openLink('https://github.com/MartinezBlaze/ProLog')"
              >Check it out on GitHub
            </b-button>
          </info-card>
        </b-col>
      </b-row>
    </content-section>
    <content-section theme="light">
      <b-row>
        <b-col class="text-center">
          <h1>Coursework & Competition Projects</h1>
          <p>
            Projects developed through university coursework, online learning,
            and competitions I’ve taken part in
          </p>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 5 -->
          <info-card id="university-classroom-timetable-system-project">
            <h2 slot="title">University Classroom Timetable System</h2>
            <p slot="tagline">
              Now, you and your lecturer never have to worry about ending up in
              the wrong class again
            </p>
            <b-img
              center
              slot="custom-image"
              :src="universityClassroomTimetableImage"
              fluid
              alt="University Classroom Timetable System"
            ></b-img>
            <div slot="description">
              <p>
                Classroom Timetable System is a desktop application with an
                integrated database engine, designed to manage classroom
                scheduling efficiently. It features user role recognition,
                allowing access to be restricted based on user type.
              </p>
              <p>The scheduler can perform key functions such as:</p>
              <ul>
                <li>Reserving classrooms</li>
                <li>Moving existing reservations</li>
                <li>Deleting reservations</li>
              </ul>
              <p>Regular users can also:</p>
              <ul>
                <li>Make new reservations</li>
                <li>Reschedule existing bookings</li>
                <li>Cancel reservations</li>
              </ul>
              <p>
                Both schedulers and users can view the weekly timetable to check
                classroom availability and usage at a glance.
              </p>
              <p class="highlighted">Utilized: C#, Microsoft Access, SQL</p>
            </div>
            <b-button
              slot="button"
              class="button-dark big-button"
              @click="
                openLink(
                  'https://github.com/MartinezBlaze/University-Classroom-Timetable-System'
                )
              "
            >
              Check it out on GitHub
            </b-button>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 6 -->
          <info-card id="body-mindfulness-project">
            <h2 slot="title">Body Mindfulness</h2>
            <p slot="tagline">True mindfulness begins from within</p>
            <b-img
              center
              slot="custom-image"
              :src="bodyMindfulnessImage"
              fluid
              alt="Body Mindfulness"
            ></b-img>
            <div slot="description">
              <p>
                Body Mindfulness is a casual iOS workout tracking app designed
                to help you stay consistent with your fitness routine. It comes
                with built-in workout sets that help you focus on specific body
                parts—so you always know what to train next.
              </p>
              <p>Key Features:</p>
              <ul>
                <li>
                  Supports multiple user accounts with personalized profiles
                </li>
                <li>Built-in BMI calculator</li>
                <li>TTracks your workout history</li>
                <li>Offers targeted workout sets by body part</li>
                <li>Includes a timer to keep you on track during sessions</li>
              </ul>
              <p>No more excuses to skip your workout, right?</p>
              <p class="highlighted">Utilized: Swift</p>
            </div>
            <b-button
              slot="button"
              class="button-dark big-button"
              @click="
                openLink('https://github.com/MartinezBlaze/BodyMindfulness')
              "
              >Check it out on GitHub
            </b-button>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 7 -->
          <info-card id="clima-project">
            <h2 slot="title">Clima</h2>
            <p slot="tagline">Rain or shine—do you need that umbrella today?</p>
            <b-img
              center
              slot="custom-image"
              :src="climaImage"
              fluid
              alt="Clima"
            ></b-img>
            <div slot="description">
              <p>
                Ever wonder what the weather’s like today? Or maybe knowing the
                forecast helps you plan your day better? Well, I’ve got just the
                app for that.
              </p>
              <p>Key Features:</p>
              <ul>
                <li>
                  Automatically checks the weather at your current location
                </li>
                <li>Lets you check the weather anywhere in the world</li>
              </ul>
              <p>
                It might not predict your destiny (yet), but at least it can
                predict the weather. One less thing to worry about in your day.
              </p>
              <p class="highlighted">Utilized: Android, Flutter</p>
            </div>
            <b-button slot="button" class="button-dark big-button" disabled
              >Currently not published on GitHub
            </b-button>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 8 -->
          <info-card id="xylophone-project">
            <h2 slot="title">Xylophone</h2>
            <p slot="tagline">Simply, a xylophone</p>
            <b-img
              center
              slot="custom-image"
              :src="xylophoneImage"
              fluid
              alt="Xylophone"
            ></b-img>
            <div slot="description">
              <p>
                The name and design say it all—this is a simple 7-note xylophone
                app. Whether you’re creating your own tune or playing a melody
                you already know, it’s all about having fun.
              </p>
              <p>Key Features:</p>
              <ul>
                <li>Bring joy to your kids</li>
                <li>Spark memories of your own happy childhood</li>
                <li>Make everyone who hears your music smile with nostalgia</li>
              </ul>
              <p>
                No instructions needed—just tap and let your inner child take
                over.
              </p>
              <p class="highlighted">Utilized: Android, Flutter</p>
            </div>
            <b-button slot="button" class="button-dark big-button" disabled
              >Currently not published on GitHub
            </b-button>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 9 -->
          <info-card id="matrix-manipulator-project">
            <h2 slot="title">Matrix Manipulator</h2>
            <p slot="tagline">Better for your karma than manipulating people</p>
            <b-img
              center
              slot="custom-image"
              :src="matrixManipulatorImage"
              fluid
              alt="Matrix Manipulator"
            ></b-img>
            <div slot="description">
              <p>
                Matrix Manipulator is a simple program built to perform various
                matrix operations, while showcasing core data structure concepts
                like memory allocation, pointers, and address handling.
              </p>
              <p>Key Features:</p>
              <ul>
                <li>Set up triangular matrices for operation</li>
                <li>Add values across rows and columns for each matrix</li>
              </ul>
              <p>
                It’s a straightforward project, but a great way to explore how
                data structures work under the hood.
              </p>
              <p class="highlighted">Utilized: C++</p>
            </div>
            <b-button
              slot="button"
              class="button-dark big-button"
              @click="
                openLink(
                  'https://github.com/MartinezBlaze/Matrix-LinkedList-Data-Structure'
                )
              "
              >Check it out on GitHub
            </b-button>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 10 -->
          <info-card id="apartment-reservation-system-project">
            <h2 slot="title">Apartment Reservation System</h2>
            <p slot="tagline">
              Bringing back the classic way of managing apartment residents
            </p>
            <b-img
              center
              slot="custom-image"
              :src="apartmentReservationSystemImage"
              fluid
              alt="Apartment Reservation System"
            ></b-img>
            <div slot="description">
              <p>
                The Apartment Reservation System is a terminal-based application
                designed to simplify the management of university apartment
                residents. Built for use in a command-line environment, it
                streamlines day-to-day tasks and keeps things organized.
              </p>
              <p>Key Features:</p>
              <ul>
                <li>Check in new tenants</li>
                <li>Check out existing tenants</li>
                <li>View a report of all current residents</li>
                <li>Search for specific tenants</li>
              </ul>
              <p class="highlighted">Utilized: Python</p>
            </div>
            <b-button
              slot="button"
              class="button-dark big-button"
              @click="
                openLink(
                  'https://github.com/MartinezBlaze/Apartment-Reservation-System'
                )
              "
              >Check it out on GitHub
            </b-button>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 11 -->
          <info-card id="justsubmit-project">
            <h2 slot="title">JustSubmit</h2>
            <p slot="tagline">
              Just submit your assignment online—no need to print anything!
            </p>
            <b-img
              center
              slot="custom-image"
              :src="justSubmitImage"
              fluid
              alt="JustSubmit"
            ></b-img>
            <div slot="description">
              <p>
                JustSubmit is a simple online assignment submission system that
                makes it easy for students to submit their work and for teachers
                to post, manage, and grade assignments—all in one place.
              </p>
              <p>For teachers, JustSubmit lets you:</p>
              <ul>
                <li>Create, update, or remove assignments</li>
                <li>Attach or remove related files</li>
                <li>
                  View student submissions, then return them with grades and
                  feedback
                </li>
              </ul>
              <p>For students, you can:</p>
              <ul>
                <li>Join assignments posted by your teachers</li>
                <li>View assignment details and deadlines</li>
                <li>Submit your work directly through the platform</li>
                <li>Submit your work directly through the platform</li>
              </ul>
              <p>
                No more printing, emailing, or chasing deadlines—JustSubmit
                keeps everything organized and accessible online.
              </p>
              <p class="highlighted">Utilized: ASP.NET Core, Microsoft Azure</p>
            </div>
            <b-button
              slot="button"
              class="button-dark big-button"
              @click="openLink('https://github.com/MartinezBlaze/ProLog')"
              disabled
              >Currently not published on GitHub
            </b-button>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 12 -->
          <info-card id="travel-journal-project">
            <h2 slot="title">TravelJournal</h2>
            <p slot="tagline">
              One big travel journal to inspire your next vacation spot
            </p>
            <b-img
              center
              slot="custom-image"
              :src="travelJournalImage"
              fluid
              alt="TravelJournal"
            ></b-img>
            <div slot="description">
              <p>
                Not sure where to go for your next vacation? Let TravelJournal
                be your first stop. Browse through our curated travel stories,
                get inspired, and find the perfect destination—then pack your
                bags and get ready for the adventure!
              </p>
              <p>
                We’re here to help you make informed choices, so your holiday is
                not just enjoyable, but truly unforgettable. Happy travels!
              </p>
              <p class="highlighted">Utilized: HTML, CSS, JavaScript</p>
            </div>
            <b-button slot="button" class="button-dark big-button" disabled
              >Currently not published on GitHub
            </b-button>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 13 -->
          <info-card id="disaster-escape-plan-project">
            <h2 slot="title">Disaster Escape Plan</h2>
            <p slot="tagline">
              Your one-stop website for everything you need to stay prepared for
              natural disasters
            </p>
            <b-img
              center
              slot="custom-image"
              :src="disasterEscapePlanImage"
              fluid
              alt="Disaster Escape Plan"
            ></b-img>
            <div slot="description">
              <p>
                In response to the 2018 tsunami disaster in Indonesia, this
                website was created to provide essential information and
                guidance on natural disasters, which can strike anytime,
                anywhere.
              </p>
              <p>
                Users can visit the site to learn about different types of
                natural disasters and what actions to take in case of an
                emergency. It also features a handy checklist tool to help users
                prepare a go-bag—an emergency kit filled with essentials to take
                with you during a disaster.
              </p>
              <p class="highlighted">
                Utilized: HTML, CSS, JavaScript, Bootstrap
              </p>
            </div>
            <b-button slot="button" class="button-dark big-button" disabled
              >Currently not published on GitHub
            </b-button>
          </info-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <!-- PROJECT 14 -->
          <info-card id="tutor-me-project">
            <h2 slot="title">TutorMe</h2>
            <p slot="tagline">
              A glimpse into how tutoring can be personalized to fit each
              student’s unique needs
            </p>
            <b-img
              center
              slot="custom-image"
              :src="tutorMeImage"
              fluid
              alt="TutorMe"
            ></b-img>
            <div slot="description">
              <p>
                TutorMe is a website that connects students with tutors to help
                them study specific modules—and the best part? The experience
                can be tailored to fit each student’s personal learning
                preferences.
              </p>
              <p>
                Students get a list of tutors that match their needs and can
                reach out to the ones that best fit their learning style.
              </p>
              <p>
                Tutors can easily sign up, share their educational background,
                list the subjects they teach, and set their own rates for
                tutoring sessions.
              </p>
              <p class="highlighted">
                Utilized: HTML, CSS, JavaScript, Bootstrap, PHP
              </p>
            </div>
            <b-button slot="button" class="button-dark big-button" disabled
              >Currently not published on GitHub
            </b-button>
          </info-card>
        </b-col>
      </b-row>
    </content-section>
    <!-- FOOTER -->
    <footer-section theme="dark"></footer-section>
  </div>
</template>

<script>
import InformationCard from "@/components/InformationCard";
import ContentSection from "../components/ContentSection.vue";

export default {
  name: "SoftwareProjects",
  components: {
    "info-card": InformationCard,
    "content-section": ContentSection,
  },
  data() {
    return {
      // Image source link
      stuffToDoImage: require("@/assets/common/images/project/stuff-to-do-1.png"),
      prologImage: require("@/assets/common/images/project/project-prolog-3.png"),
      universityClassroomTimetableImage: require("@/assets/common/images/project/project-classroom-3.png"),
      bodyMindfulnessImage: require("@/assets/common/images/project/body-mindfulness-0.png"),
      climaImage: require("@/assets/common/images/project/clima-0.png"),
      xylophoneImage: require("@/assets/common/images/project/xylophone-0.png"),
      matrixManipulatorImage: require("@/assets/common/images/project/matrix-manipulator-0.png"),
      apartmentReservationSystemImage: require("@/assets/common/images/project/project-apartment-registration-system-3.png"),
      justSubmitImage: require("@/assets/common/images/project/justsubmit-3.png"),
      travelJournalImage: require("@/assets/common/images/project/project-traveljournal-3.png"),
      disasterEscapePlanImage: require("@/assets/common/images/project/project-disaster-escape-plan-3.png"),
      tutorMeImage: require("@/assets/common/images/project/project-tutorme-3.png"),

      // Carousel source link
      ymcCarousel: [
        {
          src: require("@/assets/common/images/project/ymc-0.png"),
        },
        {
          src: require("@/assets/common/images/project/ymc-1.png"),
        },
      ],
      galaxyShooterCarousel: [
        {
          src: require("@/assets/common/images/project/galaxy-shooter-1.png"),
        },
        {
          src: require("@/assets/common/images/project/galaxy-shooter-2.png"),
        },
      ],
    };
  },
  methods: {
    openLink(link) {
      window.open(link);
    },
  },
  mounted() {
    // reset page scroll position for multiple entry point across the pages
    window.scrollTo(0, 0);
  },
};
</script>

<style scoped>
div#sectionTitle {
  height: 500px;
  line-height: 460px;
  background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)),
    url("./../assets/common/images/project/project-1.png");
  filter: grayscale(1);
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  text-align: center;
  color: white;
  padding-bottom: 0;
  padding-top: 0;
}

div.sectionHeadline {
  display: inline-block;
  vertical-align: middle;
  line-height: normal;
}

ul {
  list-style-position: inside;
}
</style>
